import { useRef, useState, type KeyboardEventHandler } from "react";

import { InnerInput } from "./components/InnerInput";
import { InputContainer } from "./components/InputContainer";
import { InputError } from "./components/InputError";
import { InputIcon } from "./components/InputIcon";
import { InputOuterContainer } from "./components/InputOuterContainer";
import { InputTitle } from "./components/InputTitle";
import { CommonProps, IconProps } from "./store/types";
import { useHoverClickable } from "src/utils/hoverClickable";

interface InputProps extends CommonProps {
  id?: string;
  value: string;
  setValue: (value: string) => void;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  type?: "text" | "password";
  rightIcon?: IconProps | false;
}

export const Input = ({
  id,
  value,
  setValue: _setValue,
  onKeyDown,
  title,
  type = "text",
  isRequired = false,
  isDisabled = false,
  isBorderless = false,
  isFocusedAutomatically = false,
  isSpellchecked,
  error: _error = false,
  placeholder,
  width = "100%",
  autoComplete = "off",
  leftIcon,
  rightIcon,
  refInput: refInputForwarded,
}: InputProps) => {
  const refInput = useRef<HTMLInputElement>(null);

  const { isHovered, isFocused } = useHoverClickable(refInputForwarded || refInput);
  const [isModified, setIsModified] = useState(false);

  const error = _error || (isRequired && isModified && value === "" && `${title} is required`);

  const setValue = (value: string) => {
    _setValue(value);
    setIsModified(true);
  };

  return (
    <InputOuterContainer width={width} isDisabled={isDisabled}>
      <InputContainer
        width={width}
        isHovered={isHovered}
        isFocused={isFocused}
        isDisabled={isDisabled}
        isBorderless={isBorderless}
      >
        <InputTitle
          isActive={isFocused}
          isRequired={isRequired}
          isDisabled={isDisabled}
          label={title}
          error={error}
        />

        <InputIcon side="left" icon={leftIcon} isDisabled={isDisabled} />

        <InnerInput
          refInput={refInputForwarded || refInput}
          id={id}
          type={type}
          value={value}
          setValue={setValue}
          onKeyDown={onKeyDown}
          name={(title || "input").toLowerCase()}
          placeholder={placeholder}
          autoComplete={autoComplete}
          isDisabled={isDisabled}
          isFocusedAutomatically={isFocusedAutomatically}
          isSpellchecked={isSpellchecked}
          paddingLeft={Boolean(leftIcon)}
          paddingRight={Boolean(rightIcon)}
        />

        <InputIcon side="right" icon={rightIcon} isDisabled={isDisabled} />
      </InputContainer>

      <InputError error={error} />
    </InputOuterContainer>
  );
};
